import type { FunctionComponent } from 'react';
import React, { Suspense, useContext, useEffect } from 'react';
import cx from 'classnames';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { DynamicButton } from '@trello/dynamic-tokens';
import { ChunkLoadErrorBoundary } from '@trello/error-reporting';
import { forNamespace } from '@trello/i18n';
import { usePopover } from '@trello/nachos/popover';
import { Tooltip } from '@trello/nachos/tooltip';
import { useLazyComponent } from '@trello/use-lazy-component';

// eslint-disable-next-line no-restricted-imports
import { Controller } from 'app/scripts/controller';
import { showingAutomaticReports } from 'app/scripts/controller/getCurrentBoardView';
import { Auth } from 'app/scripts/db/Auth';
import { ResponsiveBoardHeaderContext } from 'app/src/components/BoardHeader/ResponsiveBoardHeaderContext';
import { ButlerAlert, showButlerAlert } from '../showButlerAlert';
import { toggleButler } from '../showButlerDirectory';
import { useHasButlerAccess } from '../useHasButlerAccess';
import { useButlerPluginDataQuery } from './ButlerPluginDataQuery.generated';

import styles from './ButlerBoardButtons.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import automationDarkSvg from 'resources/images/butler/automation-dark.svg';
// eslint-disable-next-line @trello/assets-alongside-implementation
import automationLightSvg from 'resources/images/butler/automation-light.svg';

const format = forNamespace('butler');

export interface ButlerBoardButtonsProps {
  idBoard: string;
  idOrganization: string;
  renderTraceId: string;
}

export const ButlerBoardButtons: FunctionComponent<ButlerBoardButtonsProps> = ({
  idBoard,
  idOrganization,
  renderTraceId,
}) => {
  const { data } = useButlerPluginDataQuery({
    variables: { idBoard },
    waitOn: ['CurrentBoardFull'],
  });
  const isLightBackground =
    data?.board?.prefs?.backgroundBrightness === 'light';

  const hasButlerAccess = useHasButlerAccess(idBoard);

  const getIconUrl = () => {
    return isLightBackground ? `${automationDarkSvg}` : `${automationLightSvg}`;
  };

  useEffect(() => {
    Analytics.taskSucceeded({
      taskName: 'view-butlerButtons/board',
      source: 'boardScreen',
      traceId: renderTraceId,
    });
  }, [renderTraceId]);

  const ButlerBoardButtonPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "butler-board-button-popover" */ './ButlerBoardButtonPopover'
      ),
    { namedImport: 'ButlerBoardButtonPopover' },
  );

  const {
    popoverProps: butlerBoardBtnPopoverProps,
    triggerRef: butlerBoardBtnTriggerRef,
    toggle: toggleButlerBoardBtnPopover,
    push: pushButlerBoardBtnPopoverScreen,
    pop: popButlerBoardBtnPopoverScreen,
    hide: hideButlerBoardBtnPopover,
  } = usePopover<HTMLButtonElement>();

  const onClickButlerButton = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'automationBoardButton',
      source: 'boardScreen',
      containers: formatContainers({
        idBoard,
        idOrganization,
      }),
    });

    const currentBoardView = Controller.getCurrentBoardView();
    if (showingAutomaticReports()) {
      currentBoardView?.closeAutomaticReports();
    } else if (!Auth.confirmed()) {
      showButlerAlert(ButlerAlert.Unauthorized);
    } else if (!currentBoardView?.isButlerViewActive()) {
      toggleButlerBoardBtnPopover();
      Analytics.sendScreenEvent({
        name: 'butlerReportInlineDialog',
      });
    } else {
      toggleButler();
    }
  };

  const { isComponentCollapsed } = useContext(ResponsiveBoardHeaderContext);
  const isCollapsed = isComponentCollapsed('AutomationDirectoryButton');

  if (!hasButlerAccess) {
    return null;
  }

  return (
    <>
      <div>
        <Tooltip content={format('automation')}>
          <DynamicButton
            className={cx(
              styles.butlerBoardButton,
              isCollapsed && styles.hideText,
            )}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={onClickButlerButton}
            ref={butlerBoardBtnTriggerRef}
            tabIndex={0}
            aria-label={format('automation')}
          >
            <span
              className={cx(
                'icon-sm',
                styles.boardButtonIcon,
                isLightBackground &&
                  styles['boardButtonIcon--isLightBackground'],
              )}
              style={{
                backgroundImage: `url(${getIconUrl()})`,
              }}
            />
            <span className={styles.buttonText}>{format('automation')}</span>
          </DynamicButton>
        </Tooltip>
      </div>
      <ChunkLoadErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          {butlerBoardBtnPopoverProps.isVisible && (
            <ButlerBoardButtonPopover
              idBoard={idBoard}
              popoverProps={butlerBoardBtnPopoverProps}
              push={pushButlerBoardBtnPopoverScreen}
              pop={popButlerBoardBtnPopoverScreen}
              hide={hideButlerBoardBtnPopover}
            />
          )}
        </Suspense>
      </ChunkLoadErrorBoundary>
    </>
  );
};
